import { useApolloClient } from '@apollo/client/react';
import { Formik } from 'formik';
import NewCommentForm, { newCommentSchema } from './new-comment-form';
import { createEventCommentMutation, eventCommentsQuery } from '../../apollo/events';
import { useAppContext } from '../../contexts/app';
import { processMutationError } from '../../utils/formik';
import { eventPath } from '../../utils/slugify';
import LoginRequiredNotice from '../login-required-notice';
import type { Event } from '../../types/Event';

type Props = {
  event?: Event;
  disabled?: boolean;
};

const NewEventComment = ({ event, disabled }: Props) => {
  const { isLoggedIn } = useAppContext();
  const client = useApolloClient();
  if (!isLoggedIn) {
    return (
      <LoginRequiredNotice
        returnTo={eventPath(event.id, event.name)}
        action="comment"
        compact
      />
    );
  }

  if (event.status !== 'PUBLISHED') {
    return (
      <p>Comments are disabled because event is not yet published.</p>
    );
  }

  return (
    <Formik
      initialValues={{
        body: null
      }}
      validationSchema={newCommentSchema}
      onSubmit={async (values, actions) => {
        client.mutate({
          mutation: createEventCommentMutation,
          variables: {
            eventId: event.id,
            data: values,
          },
          update: (proxy, { data: { createEventComment: newComment } }) => {
            const eventCommentsVariables = { eventId: event.id };
            const data = proxy.readQuery({
              query: eventCommentsQuery,
              variables: eventCommentsVariables,
            });
            proxy.writeQuery({
              query: eventCommentsQuery,
              data: {
                ...data,
                event: {
                  ...data.event,
                  comments: [...data.event.comments, newComment],
                  commentCount: data.event.commentCount + 1,
                },
              },
              variables: eventCommentsVariables,
            });
          },
        }).then(() => {
          actions.setSubmitting(false);
          actions.resetForm();
        }).catch((error) => {
          processMutationError(error, actions);
        });
      }}
    >
      <NewCommentForm
        disabled={disabled}
        name={`new-event-comment-${event.id}`}
      />
    </Formik>
  );
}

export default NewEventComment;
